import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Plus, Trash2, Star, Edit2, X, 
  MessageCircle, Mail, Search as GoogleIcon, Image, Upload, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceIcons = {
  google: GoogleIcon,
  message: MessageCircle,
  email: Mail
};

const sourceLabels = {
  google: 'Google',
  message: 'Message',
  email: 'Email'
};

export const AdminTestimonials = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    author_name: '',
    text: '',
    rating: 5,
    date: '',
    source: 'message',
    image_url: '',
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = () => {
    setEditingId(null);
    setFormData({
      author_name: '',
      text: '',
      rating: 5,
      date: '',
      source: 'message',
      image_url: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      author_name: testimonial.author_name,
      text: testimonial.text,
      rating: testimonial.rating,
      date: testimonial.date || '',
      source: testimonial.source || 'google',
      image_url: testimonial.image_url || '',
      is_active: testimonial.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.image_url) {
      toast.error('Veuillez ajouter une image');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        author_name: formData.author_name || 'Témoignage',
        text: formData.text || ''
      };
      
      if (editingId) {
        await axios.put(
          `${API_URL}/admin/testimonials/${editingId}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Témoignage modifié');
      } else {
        await axios.post(
          `${API_URL}/admin/testimonials`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Témoignage ajouté');
      }
      setShowModal(false);
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Témoignage supprimé');
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (testimonial) => {
    try {
      await axios.put(
        `${API_URL}/admin/testimonials/${testimonial.id}`,
        { ...testimonial, is_active: !testimonial.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API_URL}/admin/upload/testimonial-image`,
        formDataUpload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      setFormData({ ...formData, image_url: response.data.image_url });
      toast.success('Image uploadée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-sand-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-serif text-sage-700">Témoignages</h1>
          </div>
          <Button
            onClick={openNewModal}
            className="bg-declic hover:bg-declic/90 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Liste */}
      <div className="px-6 py-4 space-y-3">
        {testimonials.length === 0 ? (
          <div className="text-center py-12 text-sage-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun témoignage</p>
            <p className="text-sm">Cliquez sur "Ajouter" pour créer le premier</p>
          </div>
        ) : (
          testimonials.map((testimonial) => {
            const SourceIcon = sourceIcons[testimonial.source] || MessageCircle;
            return (
              <div
                key={testimonial.id}
                className={`bg-white rounded-xl border overflow-hidden ${
                  testimonial.is_active ? 'border-sand-200' : 'border-red-200 bg-red-50/30'
                }`}
              >
                {/* Image principale */}
                {testimonial.image_url && (
                  <img 
                    src={testimonial.image_url.startsWith('/api') 
                      ? `${process.env.REACT_APP_BACKEND_URL}${testimonial.image_url}`
                      : testimonial.image_url
                    } 
                    alt="Témoignage"
                    className="w-full object-cover max-h-64"
                  />
                )}
                
                {/* Infos et actions */}
                <div className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-sage-500">
                    <SourceIcon className="w-3 h-3" />
                    <span>{sourceLabels[testimonial.source]}</span>
                    {testimonial.author_name && testimonial.author_name !== 'Témoignage' && (
                      <span>• {testimonial.author_name}</span>
                    )}
                    {!testimonial.is_active && (
                      <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded">Masqué</span>
                    )}
                  </div>
                  
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditModal(testimonial)}
                      className="h-8 w-8"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(testimonial.id)}
                      className="h-8 w-8 text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-serif text-sage-700">
                {editingId ? 'Modifier' : 'Nouveau'} témoignage
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Image en premier - champ principal */}
              <div>
                <Label className="text-sage-700 font-medium">Capture d'écran du témoignage *</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                {formData.image_url ? (
                  <div className="mt-2 relative">
                    <img 
                      src={formData.image_url.startsWith('/api') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`
                        : formData.image_url
                      } 
                      alt="Aperçu" 
                      className="w-full rounded-lg"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2 h-24 border-dashed border-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Upload en cours...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Ajouter une capture d'écran
                      </>
                    )}
                  </Button>
                )}
                <p className="text-xs text-sage-400 mt-1">
                  JPG, PNG ou WebP (max 5 Mo)
                </p>
              </div>

              {/* Champs optionnels repliables */}
              <details className="border border-sand-200 rounded-lg">
                <summary className="px-4 py-3 cursor-pointer text-sage-600 text-sm font-medium">
                  Options supplémentaires (facultatif)
                </summary>
                <div className="px-4 pb-4 space-y-3">
                  <div>
                    <Label className="text-sage-600 text-sm">Nom (si pas visible sur l'image)</Label>
                    <Input
                      value={formData.author_name}
                      onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                      placeholder="Marie D."
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sage-600 text-sm">Source</Label>
                    <div className="flex gap-2 mt-1">
                      {['google', 'message', 'email'].map((source) => {
                        const Icon = sourceIcons[source];
                        return (
                          <button
                            key={source}
                            type="button"
                            onClick={() => setFormData({ ...formData, source })}
                            className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${
                              formData.source === source
                                ? 'border-sage-500 bg-sage-50'
                                : 'border-sand-200'
                            }`}
                          >
                            <Icon className="w-3 h-3" />
                            <span>{sourceLabels[source]}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </details>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_active" className="text-sage-700">
                  Afficher sur le site
                </Label>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!formData.image_url || uploading}
                className="w-full h-12 bg-sage-600 hover:bg-sage-600 text-white rounded-xl"
              >
                {editingId ? 'Enregistrer' : 'Ajouter le témoignage'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
